import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, Renderer2, SimpleChanges, ViewChild } from "@angular/core";
import { NgxPopperjsDirective, NgxPopperjsModule, NgxPopperjsPlacements, NgxPopperjsTriggers } from "ngx-popperjs";
import { FormsModule } from "@angular/forms";
import { AsyncPipe, NgClass, NgFor } from "@angular/common";
import { PopperHelper } from "@nts/std/utility";
import { IMaskModule } from "angular-imask";
import { SvgIconComponent } from "@ngneat/svg-icon";
import { LoaderComponent } from "../../../../shared/loader/loader.component";
import { BehaviorSubject, map, merge, Observable, Subject } from "rxjs";
import { FilledButtonComponent, FilledButtonType } from "../../../../shared/buttons/filled-button/filled-button.component";
import { BaseError } from "../../../../../messages/base-error";

export enum FileProcessMode {
    Upload = 'upload',
    Base64 = 'base64'
}

export interface FileInterface<TUploadResult = any> {
    fileName?: string;
    fileContent?: string; // base64
    uploadResult?: TUploadResult;
}

@Component({
    selector: 'nts-base-file-upload-box',
    templateUrl: './base-file-upload-box.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./base-file-upload-box.component.scss'],
    standalone: true,
    imports: [
        FormsModule,
        NgxPopperjsModule,
        AsyncPipe,
        NgFor,
        SvgIconComponent,
        LoaderComponent,
        NgClass,
        FilledButtonComponent
]
})
export class BaseFileUploadBoxComponent implements OnInit, OnDestroy, OnChanges {

    @Input() processMode: FileProcessMode = FileProcessMode.Upload;
    @Input() tabIndex = -1;
    @Input() isDisabled = false;
    @Input() fileName = '';
    @Input() isReadonly = false;
    @Input() placeholder = '';
    @Input() customClasses = '';
    @Input() errorList: string[] = [];
    @Input() showErrorTooltip = true;
    @Input() showErrorBorder = true;
    @Input() defaultBorderColor = null;
    @Input() activeBorderColor = null;
    @Input() hoverBorderColor = null;
    @Input() field = null;
    @Input() addFileText = 'Aggiungi file';
    @Input() removeFileText = 'Rimuovi file';
    @Input() validateFileFunc: (file: File) => BaseError[] = null;
    @Input() uploadFileFunc: (file: File) => Promise<FileInterface> = null;
    @Input() accept: string = null; // es. ".xml,.pdf"

    @Output() onBlur = new EventEmitter();
    @Output() onFocus = new EventEmitter();
    @Output() onProcessResultChange = new EventEmitter<FileInterface>();
    @Output() onFileChange = new EventEmitter<File>();

    @ViewChild(NgxPopperjsDirective, { static: true }) popperError: NgxPopperjsDirective;
    @ViewChild('inputFile') inputFile: ElementRef;

    ngxPopperjsTriggers = NgxPopperjsTriggers;
    ngxPopperjsPlacements = NgxPopperjsPlacements;
    overrideBorderColor = null;
    isActive = false;
    isHover = false;

    showProgressBar$ = new BehaviorSubject<boolean>(false);
    isVisibleUploadAction$ = new BehaviorSubject<boolean>(true);
    files$ = new BehaviorSubject<Array<File>>([]);
    fileDescription$ = new BehaviorSubject<string>('');
    filledButtonType = FilledButtonType;

    constructor(
        public readonly el: ElementRef,
        private readonly renderer: Renderer2,
        private readonly zone: NgZone,
        private readonly cd: ChangeDetectorRef,
    ) {
    }

    ngOnInit() {
        this.handleOverridesColors();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['defaultBorderColor'] || changes['activeBorderColor'] || changes['hoverBorderColor']) {
            this.handleOverridesColors();
        }

        if (changes['errorList']) {
            this.checkPopper();
        }

        if (changes['fileName']) {

            this.checkFileName();

            this.cd.detectChanges();
        }
    }

    checkFileName() {

        if (this.files$.value?.length === 1 && this.inputFile.nativeElement.files[0]=== this.files$.value[0]) {
            if (this.fileName === this.inputFile.nativeElement.files[0].name) {
                // non ci sono cambiamenti
                return;
            }
        }

        this.isVisibleUploadAction$.next(this.fileName == null || this.fileName.length === 0);

        if (this.inputFile) {
            this.inputFile.nativeElement.value = null;
        }
        this.files$.next([]);
        this.onProcessResultChange.emit(null);

        this.updateFileDescription(this.fileName);
    }

    focus(e): void {
        this.isActive = true;
        this.handleOverridesColors();
        this.cd.detectChanges();
        this.onFocus.emit(e);
    }

    blur(e) {
        this.popperError?.hide();
        this.cd.detectChanges();
        this.onBlur.emit(e);
    }

    ngOnDestroy() {
    }

    mouseEnter(e) {
        this.isHover = true;
        this.handleOverridesColors();
    }

    mouseLeave(e) {
        this.isHover = false;
        this.handleOverridesColors();
    }

    handleOverridesColors() {
        if (!this.defaultBorderColor || !this.activeBorderColor || !this.hoverBorderColor) {
            // devono essere impostate tutte e tre le variabili
            return;
        }

        this.overrideBorderColor = this.defaultBorderColor;
        if (this.isActive && !this.isDisabled) {
            this.overrideBorderColor = this.activeBorderColor;
        }
        if (this.isHover && !this.isDisabled) {
            this.overrideBorderColor = this.hoverBorderColor;
        }
    }

    shade(color: string, percent: number) {
        var f = parseInt(color.slice(1), 16),
            t = percent < 0 ? 0 : 255,
            p = percent < 0 ? percent * -1 : percent,
            R = f >> 16, G = f >> 8 & 0x00FF,
            B = f & 0x0000FF;
        return "#" + (0x1000000 + (Math.round((t - R) * p) + R) * 0x10000 + (Math.round((t - G) * p) + G) * 0x100 + (Math.round((t - B) * p) + B)).toString(16).slice(1);
    }

     async fileSelected(event) {
    
        const file: File = event.target.files[0];
        this.files$.next([file]);
        this.onFileChange.emit(file);
        this.errorList = this.validateFile(file)?.map(e => e.description);
        this.updateFileDescription(file.name);
        this.toggleUploadVisibilityBasedOnFileName(file.name);
        await this.applyFileLogic();
        
    }

    async applyFileLogic() {
        this.showProgressBar$.next(true);
        switch (this.processMode) {
            case FileProcessMode.Upload:
                let uploadFileRes:FileInterface = null;
                if (this.errorList?.length === 0) {
                    uploadFileRes = await this.uploadFileFunc(this.files$.value[0]);
                }
                this.onProcessResultChange.emit(uploadFileRes);
                break;
            case FileProcessMode.Base64:
                let base64: string = null;
                if (this.errorList?.length === 0) {
                    base64 = await this.convertFileToBase64(this.files$.value[0]);
                }
                this.onProcessResultChange.emit({fileContent: base64});
                break;
            default:
                throw new Error('Not supported storage mode: ' + this.processMode);

        }
        this.showProgressBar$.next(false);
    }

    convertFileToBase64(file: File): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                let base64String = reader.result as string;
                if (base64String?.length> 0) {
                    // Rimuovi l'intestazione "data:mime/type;base64," se presente
                   base64String = base64String.split(',')[1];
                }
                resolve(base64String);
            };
            reader.onerror = (error) => {
                reject(error);
            };
            reader.readAsDataURL(file);
        });
    }

    validateFile(file: File): BaseError[] {
        if (this.validateFileFunc) {
            return this.validateFileFunc(file);
        }
        return [];
    }

    reset() {
        this.fileDescription$.next(null);
        if (this.inputFile) {
            this.inputFile.nativeElement.value = null;
        }
        this.fileName = null
        this.files$.next([]);
        this.onFileChange.emit(null);
        this.onProcessResultChange.emit(null);
    }

    canUploadFile(): Observable<boolean> {
        return merge(
            this.showProgressBar$,
            this.files$,
        ).pipe(map(() => {
            return this.files$.value != null &&
                Array.from(this.files$.value).length > 0 &&
                !this.showProgressBar$.value
        }))
    }

    private toggleUploadVisibilityBasedOnFileName(newFileName: string) {
        this.isVisibleUploadAction$.next(newFileName == null || newFileName.length === 0);
    }

    private updateFileDescription(description: string): void {
        if (description == null || description.length === 0) {
            this.fileDescription$.next('');
            return;
        }
        this.fileDescription$.next('<div> <p>' + description + '</p> </div>');
    }

    private checkPopper() {
        if (this.popperError && (this.errorList == null || this.errorList.length === 0)) {
            PopperHelper.hide(this.popperError);
        }
        // if (this.popperInfo && this.errorList?.length > 0) {
        //     PopperHelper.hide(this.popperInfo);
        // }
    }
}
